import { GenericAbortSignal } from 'axios';
import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';
import { api } from '@/api';
import { PaginationSearchParams } from '@/api/types';
import {
  AircraftAvailabilityView,
  EmployeeAvailabilityData,
  EmployeeRole,
  ResourcesAvailabilityParams,
} from '@/api/apis/resources';
import { queryClient } from '@/queries';
import { caseQueries } from '@/queries/case';
import { hubQueries } from '@/queries/hub';
import { homeLocationQueries } from '@/queries/homelocation';
import { getComposedEmployeeData, getItemsWithAffiliationFacilities } from '@/queries/employee';
import { regionsQueries } from '@/queries/regions';
import { resourcesQueryKeys } from '@/queries/resource/resources.keys';

const allHubsPaginationParams: PaginationSearchParams = {
  itemsPerPage: 100,
  pageIndex: 0,
};

const getAircraftAvailability = async (
  params: ResourcesAvailabilityParams,
  signal: GenericAbortSignal,
): Promise<AircraftAvailabilityView[]> => {
  const aircraftsList = await api.resource.getAircraftResourcesAvailability(params, signal);

  return Promise.all(
    aircraftsList.map(async (aircraft) => {
      const reservations = await Promise.all(
        aircraft.reservations.map(async (r) => {
          try {
            const caseData = await queryClient.ensureQueryData(caseQueries.item(r.caseId));

            return {
              ...r,
              donorId: caseData.donorId,
              organType: caseData.organType,
            };
          } catch {
            return r;
          }
        }),
      );

      return {
        ...aircraft,
        reservations,
      };
    }),
  );
};

const getSurgeonsAvailability = async (
  params: Partial<ResourcesAvailabilityParams>,
  signal: GenericAbortSignal,
): Promise<EmployeeAvailabilityData[]> => {
  const hubsList = await queryClient.ensureQueryData(hubQueries.list(allHubsPaginationParams));
  const employees = await api.resource.getEmployeeResourcesAvailability(params, signal);

  let surgeons = employees.items.filter((e) => e.roles.includes(EmployeeRole.RetrievalSurgeon));
  const homeLocationIds = surgeons.reduce<string[]>(
    (acc, item) =>
      queryClient.getQueryData(homeLocationQueries.item(item.homeLocation).queryKey)
        ? acc
        : [...acc, item.homeLocation],
    [],
  );
  const filteredIds = Array.from(new Set(homeLocationIds));

  await queryClient.ensureQueryData(homeLocationQueries.list({ ids: filteredIds.join(',') }));

  if (params.isContractor) {
    surgeons = await getItemsWithAffiliationFacilities(surgeons);
  }

  return Promise.all(
    surgeons.map(async (item) => {
      const currentItem = item;
      const baseLocation = hubsList.items.find((h) => h.id === currentItem.baseLocation);
      if (currentItem?.sShiftAvailabilityData?.currentShift) {
        const { currentShift } = currentItem.sShiftAvailabilityData;
        currentItem.sShiftAvailabilityData.currentShift = {
          ...currentShift,
          region: (await queryClient.ensureQueryData(regionsQueries.all())).find((r) => r.id === currentShift.regionId)
            ?.name,
        };
      }

      if (currentItem.reservations.length > 0) {
        const caseData = await queryClient.ensureQueryData(caseQueries.item(currentItem.reservations[0].caseId));
        currentItem.reservations = [
          {
            ...currentItem.reservations[0],
            caseName: caseData.caseName,
            donorId: caseData.donorId,
            organType: caseData.organType,
          },
        ];
      }

      if (!currentItem.homeLocation) {
        return {
          ...currentItem,
          baseLocationObject: baseLocation,
        };
      }

      const homeLocation = await queryClient.ensureQueryData(homeLocationQueries.item(currentItem.homeLocation));

      return {
        ...currentItem,
        baseLocationObject: baseLocation,
        homeLocationObject: homeLocation,
      };
    }),
  );
};

export const resourceAvailabilityQueries = {
  aircrafts: ({ caseId, ...params }: ResourcesAvailabilityParams & { caseId: string }) =>
    queryOptions({
      queryKey: resourcesQueryKeys.getAvailableAircrafts(caseId, params),
      queryFn: ({ signal }) => getAircraftAvailability(params, signal),
      placeholderData: keepPreviousData,
    }),
  surgeons: ({ caseId, ...params }: ResourcesAvailabilityParams & { caseId: string }) =>
    queryOptions({
      queryKey: resourcesQueryKeys.getAvailableRS(caseId, params),
      queryFn: ({ signal }) => getSurgeonsAvailability(params, signal),
      placeholderData: keepPreviousData,
    }),
  devices: ({ caseId, ...params }: ResourcesAvailabilityParams & { caseId: string }) =>
    queryOptions({
      queryKey: resourcesQueryKeys.getAvailableDevices(caseId, params),
      queryFn: ({ signal }) => api.resource.getDeviceResourcesAvailability(params, signal),
      placeholderData: keepPreviousData,
    }),
  employeeById: ({ employeeId }: { employeeId: string }) =>
    queryOptions({
      queryKey: resourcesQueryKeys.getAvailableEmployee(employeeId),
      queryFn: async ({ signal }) => {
        const employeeResponse = await api.resource.getEmployeeResourcesAvailability(
          { employeeId, reserved: true },
          signal,
        );

        return getComposedEmployeeData(employeeResponse.items[0]);
      },
    }),
  aircraftById: ({ aircraftIds }: { aircraftIds: string }) =>
    queryOptions({
      queryKey: resourcesQueryKeys.getAvailableAircraftsById(aircraftIds),
      queryFn: ({ signal }) => getAircraftAvailability({ reserved: true, aircraftIds }, signal),
      placeholderData: keepPreviousData,
    }),
};

export const useAircraftAvailability = (params: ResourcesAvailabilityParams & { caseId: string }) => {
  return useQuery(resourceAvailabilityQueries.aircrafts(params));
};

export const useDevicesAvailability = (
  params: ResourcesAvailabilityParams & { caseId: string },
  enabled: boolean = true,
) => {
  return useQuery({ ...resourceAvailabilityQueries.devices(params), enabled });
};

export const useAvailabilityEmployeeById = (employeeId: string) => {
  return useQuery(resourceAvailabilityQueries.employeeById({ employeeId }));
};

export const useAircraftsById = ({ aircraftIds }: { aircraftIds: string }) => {
  return useQuery(resourceAvailabilityQueries.aircraftById({ aircraftIds }));
};
